import type { DirectiveBinding } from 'vue';
import { markdown as markdownParser } from 'very-small-parser';
import { toHast as mdAstToHtmlAst } from 'very-small-parser/lib/markdown/block/toHast';
import { toText as htmlAstToTextHtml } from 'very-small-parser/lib/html/toText';
import type { TBlockToken } from 'very-small-parser/lib/markdown';
import type { THtmlToken, IElement, IRoot, IText, IComment } from 'very-small-parser/lib/html/types';
import type { AtLeastOneKey } from '@/types';

/** Liste de tag HTML autorisé à être rendu via le Markdown */
export type MarkdownSupportedTags = 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'ul' | 'ol' | 'li' | 'hr' | 'br' | 'a' | 'strong' | 'em' | 'del' | 'u' | 'sub' | 'sup';

/** Classe CSS a appliqué sur les élément HTML rendu via le Markdown */
export type MarkdownClasses = AtLeastOneKey<Record<MarkdownSupportedTags, string>>;

/** Attributs accepter et géré par la directive v-markdown  */
export type MarkdownBindingAttrs = AtLeastOneKey<{
  classes?: MarkdownClasses;
  allowedTags?: MarkdownSupportedTags[];
}>;

export type MarkdownBinding =
  | string
  | { text: string } & MarkdownBindingAttrs;

/**
 * List complete des éléments de type block supporté par la librairie very-small-parser
 */
const blockElement: string[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'li', 'hr', 'pre', 'code', 'blockquote', 'table', 'thead', 'tr', 'tbody', 'div', 'br'];
/**
 * Élément qui doit remplacer les éléments block qui ne sont pas supporté par l'instance de markdown en fonction de la
 * constante `supportedElements` ou encore de la liste `allowedTags` fournis via le binding de la directive.
 */
const blockFallbackTagName = 'div';
/**
 * Élément qui doit remplacer les éléments inline qui ne sont pas supporté par l'instance de markdown en fonction de la
 * constante `supportedElements` ou encore de la liste `allowedTags` fournis via le binding de la directive.
 */
const inlineFallbackTagName = 'span';
/**
 * Liste par défaut des éléments HTML supporté par MonTA. Cette liste sert de limitation globale, mais e;;e peut être
 * restreint par la propriété `allowedTags` défini par l'instance au niveau du binding de la directive.
 */
const supportedElements: string[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'li', 'hr', 'br', 'div', 'a', 'strong', 'em', 'del', 'u'];

/**
 * Methode récursive pour désinfecté un Hast (Html Abstract Syntax Tree) et tout ces enfants.
 *
 * ***Fait partie intégrante de la désinfection du code (code sanitizing)***
 */
const sanitizeHast = <T extends (THtmlToken | IRoot | IText | IComment | IElement)>(hast: T, attrs?: MarkdownBindingAttrs): T => {
  const results = { ...hast };
  switch (results.type) {
    case 'root':
      results.children = results.children.map(child => sanitizeHast(child, attrs));
      break;
    case 'element':
      sanitizeElement(results, attrs);
      break;
  }
  return results;
};
/**
 * Évalue si un tag HTML est autorisé à être utilisé en fonction de la liste globale d'éléments autorisée et de la
 * limitation de l'instance actuelle via l'attribut `allowedTags`.
 * Si le `tagName` est inclus dans `supportedElements`, il est donc de type `MarkdownSupportedTags`.
 *
 * ***Fait partie intégrante de la désinfection du code (code sanitizing)***
 */
const isAllowedTag = (tagName: string | MarkdownSupportedTags, allowedTags?: MarkdownSupportedTags[]): boolean => {
  if (supportedElements.includes(tagName)) {
    return Array.isArray(allowedTags) ? allowedTags.includes(tagName as MarkdownSupportedTags) : true;
  }
  return false;
};

const applyFallback = (el: IElement, isBlock: boolean, isBlockFallbackAllowed: boolean) => {
  el.tagName = isBlock && isBlockFallbackAllowed ? blockFallbackTagName : inlineFallbackTagName;
};

/**
 * Identifie si le protocole (schema) d'un lien est accepté et sécuritaire pour être autorisé dans le HTML de sortie.
 * Si aucun protocole, le lien est considéré comme un lien relatif à l'application où une ancre dans la page et donc
 * sécuritaire.
 *
 * **Les protocoles supportés sont :**
 * - `http`
 * - `https`
 * - `mailto`
 * - `tel`
 *
 * ***Fait partie intégrante de la désinfection du code (code sanitizing)***
 */
const isSafeProtocol = (prop: string): boolean => {
  const safeProtocols = ['', 'http', 'https', 'mailto', 'tel'];
  const [, protocol] = /^(.*):/.exec(prop) ?? ['', ''];
  return safeProtocols.includes(protocol);
};

const sanitizeElement = (el: IElement, attrs?: MarkdownBindingAttrs) => {
  const { tagName: tag, properties: props, children } = el;
  const isBlock = blockElement.includes(tag);
  const elClass: string[] = [];
  const classes: MarkdownClasses | undefined = attrs && 'classes' in attrs ? attrs.classes : undefined;
  const allowedTags: MarkdownSupportedTags[] | undefined = attrs && 'allowedTags' in attrs ? attrs.allowedTags : undefined;
  const isAllowed = isAllowedTag(tag, allowedTags);
  const isBlockFallbackAllowed = isAllowedTag(blockFallbackTagName, allowedTags);

  if (props) {
    /** Liste d'attribut à risque d'injection */
    const unsafeAttributes = ['href', 'src', 'action'];
    unsafeAttributes.forEach(attr => {
      if (attr in props && typeof props[attr] !== 'undefined' && !isSafeProtocol(props[attr])) {
        delete props[attr]; // Suppression de l'attribut à risque
        props.class = 'text-red!'; // Force un affichage en rouge pour marquer l'erreur
        el.tagName = 'mark'; // Le tag sera désormais un tag <mark> qui va accentuer l'erreur.
      }
    });
  }
  if (props?.class) {
    elClass.push(props.class);
  }
  if (!isAllowed) {
    elClass.push(`v-markdown-unsupported-tag v-markdown-unsupported-tag--${tag}`);
    applyFallback(el, isBlock, isBlockFallbackAllowed);
  }
  if (classes && tag in classes && isAllowed) {
    const key = tag as keyof MarkdownClasses;
    elClass.push(classes[key] || '');
  }
  /**
   * Pour ne pas briser l'accessibilité, tous les heading h1 à h6 sont remplacé par des div.h[1-6].
   * ex.: `<h1>foo</h1>` sera remplacé par `<div class="h1">foo</div>`
   */
  if (tag.match(/^h[1-6]$/)) {
    if (isAllowed) {
      elClass.push(tag);
    }
    applyFallback(el, isBlock, isBlockFallbackAllowed);
  }
  if (elClass.length) {
    el.properties ??= {};
    el.properties.class = elClass.join(' ');
  }
  if (Array.isArray(children)) {
    el.children = children.map(child => sanitizeHast(child, attrs));
  }
};
const renderHtmlFromMarkdown = (text: string, attrs?: MarkdownBindingAttrs) => {
  const mdast: TBlockToken[] = markdownParser.block.parse(removeHtmlSupport(text));
  const hast: THtmlToken = sanitizeHast(mdAstToHtmlAst(mdast), attrs);

  return htmlAstToTextHtml(hast);
};

/**
 * Remplace tous les `<` et `>` du texte par l'équivalent encodé HTML. Ce qui prévient la possibilité d'écrire du HTML.
 *
 * ***Fait partie intégrante de la désinfection du code (code sanitizing)***
 */
const removeHtmlSupport = (input: string): string => {
  const replacer = { '<': '&lt;', '>': '&gt;' };
  return input.replace(/([<>])/g, (_, $1: '<' | '>') => replacer[$1]);
};

const applyMarkdown = (el: HTMLElement, binding: DirectiveBinding<MarkdownBinding>): void => {
  const { value, oldValue } = binding;
  const text = typeof value === 'string' ? value : value.text;
  const oldText = oldValue !== undefined && oldValue !== null
    ? (typeof oldValue === 'string' ? oldValue : oldValue.text)
    : undefined;

  el.classList.add('v-markdown-wrapper');

  if (text !== oldText) {
    const classes: MarkdownClasses | undefined = typeof value === 'string' || !('classes' in value) ? undefined : value.classes;
    const allowedTags: MarkdownSupportedTags[] | undefined = typeof value === 'string' || !('allowedTags' in value) ? undefined : value.allowedTags;

    let attrs: MarkdownBindingAttrs | undefined = undefined;

    if (classes) {
      attrs = { classes };
    }
    if (allowedTags) {
      attrs = attrs ? Object.assign(attrs, { allowedTags }) : { allowedTags };
    }
    el.innerHTML = renderHtmlFromMarkdown(text, attrs);
  }
};

export default {
  mounted(el: HTMLElement, binding: DirectiveBinding<MarkdownBinding>) {
    applyMarkdown(el, binding);
  },
  updated(el: HTMLElement, binding: DirectiveBinding<MarkdownBinding>) {
    applyMarkdown(el, binding);
  },
};
